import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { IMAGES } from "../data/content";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (id) => `${BACKEND}/api/rooms/photos/${id}`;

// Returns the list of image URLs for a room (uploaded photos, or fallback placeholder).
export const roomImages = (room) => {
  if (room?.photos && room.photos.length > 0) return room.photos.map((p) => photoUrl(p.id));
  return [IMAGES[room?.image] || IMAGES.room];
};

const STATUS_STYLES = {
  Disponible: "bg-[#7D5A44] text-white",
  Reservada: "bg-white text-[#7D5A44] border border-[#7D5A44]",
  Ocupada: "bg-[#B2967D] text-white",
};

export const RoomGalleryModal = ({ room, open, onClose }) => {
  const [index, setIndex] = useState(0);
  const images = roomImages(room);

  useEffect(() => {
    setIndex(0);
  }, [room]);

  if (!room) return null;
  const prev = () => setIndex((i) => (i - 1 + images.length) % images.length);
  const next = () => setIndex((i) => (i + 1) % images.length);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl p-0 rounded-none border-[#B2967D]/30 font-poppins overflow-hidden" data-testid="room-gallery-modal">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center gap-3 text-2xl font-bold text-[#7D5A44]">
            Habitación {room.number}
            <span className={`text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1 ${STATUS_STYLES[room.status]}`}>
              {room.status}
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Galería de fotografías de la habitación {room.number}
          </DialogDescription>
        </DialogHeader>

        {/* Carousel */}
        <div className="relative mt-4 bg-black">
          <img
            src={images[index]}
            alt={`Habitación ${room.number} foto ${index + 1}`}
            data-testid="gallery-main-image"
            className="w-full h-[360px] sm:h-[440px] object-cover"
          />
          {images.length > 1 && (
            <>
              <button
                onClick={prev}
                data-testid="gallery-prev"
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#7D5A44] p-2"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={next}
                data-testid="gallery-next"
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#7D5A44] p-2"
              >
                <ChevronRight size={22} />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    className={`h-1.5 transition-all ${i === index ? "w-6 bg-white" : "w-1.5 bg-white/50"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Thumbnails + info */}
        <div className="px-6 pb-6">
          {images.length > 1 && (
            <div className="mt-4 flex gap-2 overflow-x-auto">
              {images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  className={`shrink-0 border-2 ${i === index ? "border-[#7D5A44]" : "border-transparent"}`}
                >
                  <img src={src} alt={`mini ${i + 1}`} className="h-16 w-20 object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="mt-6 flex items-end justify-between border-t border-[#B2967D]/25 pt-5">
            <div>
              <span className="text-[11px] font-light text-[#B2967D] uppercase tracking-wide block">Superficie</span>
              <span className="text-base font-bold text-black">{room.size}</span>
            </div>
            <div className="text-right">
              <span className="text-[11px] font-light text-[#B2967D] uppercase tracking-wide block">Renta</span>
              <span className="text-xl font-bold text-[#7D5A44]">
                {room.price}
                <span className="text-xs font-light text-black/50"> MXN/mes</span>
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
